import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Trash2, Loader2, ChevronDown, ArrowRight, Swords, BookOpen,
  X, AlertCircle,
} from 'lucide-react';
import axios from 'axios';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENGINE_LABELS = {
  chatgpt: { name: 'ChatGPT', color: '#10A37F' },
  google_ai: { name: 'Google AI', color: '#4285F4' },
  perplexity: { name: 'Perplexity', color: '#1A73E8' },
  claude: { name: 'Claude', color: '#CC785C' },
  gemini: { name: 'Gemini', color: '#886FBF' },
  copilot: { name: 'Copilot', color: '#00BCF2' },
  grok: { name: 'Grok', color: '#000000' },
};

export default function Competitors() {
  const navigate = useNavigate();
  const [competitors, setCompetitors] = useState([]);
  const [userScore, setUserScore] = useState(0);
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [expandedComp, setExpandedComp] = useState(null);
  const [generatingPlaybook, setGeneratingPlaybook] = useState(null);

  const fetchCompetitors = async () => {
    try {
      const res = await axios.get(`${API}/competitors`, { withCredentials: true });
      setCompetitors(res.data.competitors || []);
      setUserScore(res.data.user_score || 0);
      setBusinessName(res.data.business_name || '');
    } catch (e) {
      if (e.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCompetitors(); }, [navigate]);

  const handleAdd = async () => {
    if (!addName.trim()) return;
    setAdding(true);
    setAddError('');
    try {
      const res = await axios.post(`${API}/competitors`, { competitor_name: addName.trim() }, { withCredentials: true });
      setCompetitors(prev => [res.data, ...prev]);
      setAddName('');
      setShowAdd(false);
    } catch (e) {
      setAddError(e.response?.data?.detail || 'Failed to add competitor');
    }
    setAdding(false);
  };

  const handleDelete = async (compId) => {
    try {
      await axios.delete(`${API}/competitors/${compId}`, { withCredentials: true });
      setCompetitors(prev => prev.filter(c => c.competitor_id !== compId));
    } catch {}
  };

  const handlePlaybook = async (compId) => {
    setGeneratingPlaybook(compId);
    try {
      const res = await axios.post(`${API}/competitors/${compId}/playbook`, {}, { withCredentials: true });
      setCompetitors(prev => prev.map(c =>
        c.competitor_id === compId ? { ...c, playbook: res.data.playbook, playbook_generated_at: res.data.generated_at } : c
      ));
    } catch {}
    setGeneratingPlaybook(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4C9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AppShell active="home">
      <div className="p-5 max-w-3xl mx-auto pb-28" data-testid="competitors-page">
        <h1 className="font-display text-2xl font-medium text-[#3C3226] tracking-tight mb-1">Competitor Playbook</h1>
        <p className="text-sm text-[#5C5347] mb-4 leading-relaxed">
          Track competitors and generate AI-powered strategies to beat them.
        </p>

        {/* Your score reference */}
        <div className="p-3 rounded-none bg-[#1D6B4F]/5 border border-[#1D6B4F]/10 mb-4 flex items-center gap-3" data-testid="your-score-ref">
          <div className="w-10 h-10 rounded-sm bg-[#1D6B4F]/15 flex items-center justify-center">
            <span className="text-sm font-bold text-[#1D6B4F]">{userScore}</span>
          </div>
          <div>
            <p className="text-xs font-medium text-[#3C3226]">{businessName || 'Your Business'}</p>
            <p className="text-[10px] text-[#5C5347]">Your AI Recommendation Score</p>
          </div>
        </div>

        {/* Add competitor */}
        {!showAdd ? (
          <button
            onClick={() => setShowAdd(true)}
            disabled={competitors.length >= 5}
            className="w-full p-3 rounded-none border-2 border-dashed border-[#D4C9B8] text-[#5C5347] flex items-center justify-center gap-2 hover:border-[#1D6B4F]/30 hover:text-[#1D6B4F] transition-all mb-4 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="add-competitor-btn"
          >
            <Plus size={16} />
            <span className="text-sm font-medium">
              {competitors.length >= 5 ? 'Max 5 competitors' : 'Add competitor to track'}
            </span>
          </button>
        ) : (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="overflow-hidden mb-4"
          >
            <div className="p-4 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] space-y-3" data-testid="add-competitor-form">
              <input
                type="text"
                value={addName}
                onChange={e => { setAddName(e.target.value); setAddError(''); }}
                placeholder="Competitor business name"
                className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3C3226]"
                data-testid="competitor-name-input"
                autoFocus
              />
              {addError && (
                <div className="flex items-center gap-1.5 text-xs text-[#E24B4A]">
                  <AlertCircle size={12} /> {addError}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowAdd(false); setAddName(''); setAddError(''); }}
                  className="flex-1 rounded-full border border-[#D4C9B8] text-[#5C5347] py-2.5 text-sm font-medium hover:bg-[#EDE8DF] transition-colors"
                  data-testid="cancel-add-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={adding || !addName.trim()}
                  className="flex-[2] rounded-none bg-[#C67A5C] hover:bg-[#B3694C] disabled:opacity-40 text-[#F0EBE3] py-2.5 text-sm font-medium transition-all flex items-center justify-center gap-2"
                  data-testid="confirm-add-btn"
                >
                  {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  {adding ? 'Analyzing...' : 'Track competitor'}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Competitor list */}
        {competitors.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-none bg-[#F7F4EF] flex items-center justify-center mx-auto mb-3">
              <Swords size={24} className="text-[#5C5347]" />
            </div>
            <p className="text-sm font-medium text-[#3C3226] mb-1">No competitors tracked</p>
            <p className="text-xs text-[#5C5347]">Add a competitor to see how you stack up and get strategies to beat them.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="competitor-list">
            {competitors.map((comp, idx) => {
              const isExpanded = expandedComp === comp.competitor_id;
              const scoreDiff = userScore - comp.estimated_score;

              return (
                <motion.div
                  key={comp.competitor_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="rounded-none bg-[#F7F4EF] border border-[#D4C9B8] overflow-hidden"
                  data-testid={`competitor-${comp.competitor_id}`}
                >
                  {/* Header */}
                  <button
                    onClick={() => setExpandedComp(isExpanded ? null : comp.competitor_id)}
                    className="w-full p-4 flex items-center gap-3 text-left"
                  >
                    <div className="w-10 h-10 rounded-sm bg-[#C67A5C]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-[#C67A5C]">{comp.estimated_score}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#3C3226] truncate">{comp.competitor_name}</p>
                      <p className="text-[11px] text-[#5C5347]">{comp.profession} in {comp.city}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs font-bold ${scoreDiff >= 0 ? 'text-[#1D6B4F]' : 'text-[#E24B4A]'}`}>
                        {scoreDiff >= 0 ? '+' : ''}{scoreDiff}
                      </span>
                      <ChevronDown size={14} className={`text-[#5C5347] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {/* Expanded details */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 space-y-3">
                          {/* Score comparison bars */}
                          <div className="space-y-2" data-testid="engine-comparison">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-[#5C5347]">AI Engine Breakdown</p>
                            {Object.entries(ENGINE_LABELS).map(([key, eng]) => {
                              const compScore = comp.ai_presence?.[key] || 0;
                              return (
                                <div key={key} className="flex items-center gap-2">
                                  <span className="text-[10px] text-[#5C5347] w-16 truncate">{eng.name}</span>
                                  <div className="flex-1 h-2 bg-[#F7F4EF] rounded-full overflow-hidden relative">
                                    <div
                                      className="h-full rounded-full"
                                      style={{ width: `${compScore}%`, backgroundColor: eng.color, opacity: 0.6 }}
                                    />
                                  </div>
                                  <span className="text-[10px] font-medium text-[#5C5347] w-6 text-right">{compScore}</span>
                                </div>
                              );
                            })}
                          </div>

                          {/* Playbook section */}
                          {comp.playbook ? (
                            <div className="p-3 rounded-sm bg-[#1D6B4F]/3 border border-[#1D6B4F]/10" data-testid="playbook-content">
                              <div className="flex items-center gap-1.5 mb-2">
                                <BookOpen size={12} className="text-[#1D6B4F]" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-[#1D6B4F]">Strategy Playbook</span>
                              </div>
                              <div className="text-xs text-[#3C3226] leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto prose-sm">
                                {comp.playbook}
                              </div>
                              <button
                                onClick={() => handlePlaybook(comp.competitor_id)}
                                disabled={generatingPlaybook === comp.competitor_id}
                                className="mt-2 text-[10px] font-medium text-[#1D6B4F] hover:underline flex items-center gap-1"
                                data-testid="regenerate-playbook-btn"
                              >
                                {generatingPlaybook === comp.competitor_id
                                  ? <><Loader2 size={10} className="animate-spin" /> Regenerating...</>
                                  : 'Regenerate playbook'}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handlePlaybook(comp.competitor_id)}
                              disabled={generatingPlaybook === comp.competitor_id}
                              className="w-full p-3 rounded-sm bg-[#C67A5C] hover:bg-[#B3694C] disabled:opacity-60 text-[#F0EBE3] text-sm font-medium transition-all flex items-center justify-center gap-2"
                              data-testid="generate-playbook-btn"
                            >
                              {generatingPlaybook === comp.competitor_id
                                ? <><Loader2 size={14} className="animate-spin" /> Generating playbook...</>
                                : <><Swords size={14} /> Generate strategy playbook</>}
                            </button>
                          )}

                          {/* Delete button */}
                          <button
                            onClick={() => handleDelete(comp.competitor_id)}
                            className="flex items-center gap-1.5 text-xs text-[#E24B4A]/60 hover:text-[#E24B4A] transition-colors"
                            data-testid="delete-competitor-btn"
                          >
                            <Trash2 size={12} /> Stop tracking
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
